import { ValueAxis } from "@/components/tool-kit/charts/axes";
import { ChartSvg, ChartTitles } from "@/components/tool-kit/charts/chart-svg";
import type { ChartStyle } from "@/components/tool-kit/charts/controls";
import { SvgLegend } from "@/components/tool-kit/charts/legend";
import { Marker, markerShape, px } from "@/components/tool-kit/charts/marks";
import { DataTable } from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2 } from "@/components/tool-kit/styles";
import { colorIndex, paletteColor } from "@/lib/tool-kit/charts/color";
import {
  MINUS,
  type NumberFormat,
  tickFormat,
  usesDecimalComma,
} from "@/lib/tool-kit/charts/format";
import {
  CHART_FONT_SIZE,
  CHART_HEIGHT,
  CHART_PADDING,
  CHART_WIDTH,
  cartesianFrame,
  emptiestCorner,
  layoutLegend,
  type Rect,
} from "@/lib/tool-kit/charts/layout";
import {
  formatCoefficient,
  formatEquation,
  formatR2,
  type LinearFit,
} from "@/lib/tool-kit/charts/regression";
import { type AxisRange, linearScale } from "@/lib/tool-kit/charts/scale";
import { maxTextWidth } from "@/lib/tool-kit/charts/text";
import { CHART_INK } from "@/lib/tool-kit/palette";

/*
 * A scatter plot: points coloured and shaped by group, optional least-squares lines
 * (dashed — a fitted line is a model, not data), and axes whose ranges need not start
 * at zero. Points and lines are clipped to the plot, so a reader-chosen range that
 * excludes some points hides them rather than drawing them over the axes.
 */

export interface ScatterPoint {
  x: number;
  y: number;
  /** Group slot, 0-based (`groupIndex`). */
  group: number;
  /** A stable key — the source line number does. */
  key: string | number;
}

export interface ScatterFit {
  name: string;
  color: string;
  fit: LinearFit | null;
}

/** Room between the plot's edge and the outermost points. */
const INSET = 8;

/**
 * "(2.5, 61)" — or "(2,5; 61)" where the decimal mark is a comma, so the separator
 * between the coordinates cannot be mistaken for one.
 */
export function pointLabel(x: number, y: number, fmt: NumberFormat): string {
  const separator = usesDecimalComma(fmt) ? "; " : ", ";
  const options = { maximumFractionDigits: 2 };
  return `(${fmt(x, options)}${separator}${fmt(y, options)})`;
}

/**
 * The fitted equations, in whichever corner of the plot hides the fewest points, on a
 * white backing so gridlines do not run through the text.
 */
function EquationBlock({
  fits,
  plot,
  points,
  fmt,
}: {
  fits: ScatterFit[];
  plot: Rect;
  /** Drawn point positions, to keep the block off the data. */
  points: (readonly [number, number])[];
  fmt: NumberFormat;
}) {
  const size = CHART_FONT_SIZE.value;
  const lineHeight = size + 5;
  const lines = fits.map((entry) => {
    const fit = entry.fit as LinearFit;
    const prefix = fits.length > 1 ? `${entry.name}: ` : "";
    return `${prefix}${formatEquation(fit, fmt)}, r² = ${formatR2(fit, fmt)}`;
  });
  const width = maxTextWidth(lines, { fontSize: size }) + 12;
  const height = lines.length * lineHeight + 6;
  const corner = emptiestCorner(plot, width, height, points);
  return (
    <g>
      <rect
        fill={CHART_INK.background}
        fillOpacity={0.85}
        height={px(height)}
        width={px(width)}
        x={px(corner.x)}
        y={px(corner.y)}
      />
      {lines.map((line, row) => (
        <text
          fill={CHART_INK.text}
          fontSize={size}
          key={line}
          x={px(corner.x + 6)}
          y={px(corner.y + 2 + size + row * lineHeight)}
        >
          {line}
        </text>
      ))}
    </g>
  );
}

export function ScatterChart({
  id,
  points,
  names,
  fits,
  xRange,
  yRange,
  showEquation,
  style,
  fmt,
  label,
  description,
}: {
  /** The `<svg>` id, unique on the page; also prefixes the clip path. */
  id: string;
  points: ScatterPoint[];
  /** Group names; more than one draws a legend. */
  names: string[];
  fits: ScatterFit[];
  xRange: AxisRange;
  yRange: AxisRange;
  showEquation: boolean;
  style: ChartStyle;
  fmt: NumberFormat;
  label: string;
  description: string;
}) {
  const xTick = tickFormat(fmt, xRange.decimals);
  const yTick = tickFormat(fmt, yRange.decimals);
  const legend =
    names.length > 1
      ? layoutLegend(names, CHART_WIDTH - 2 * CHART_PADDING)
      : null;
  const frame = cartesianFrame({
    width: CHART_WIDTH,
    height: CHART_HEIGHT,
    title: style.apa ? "" : style.title,
    xTitle: style.xTitle,
    yTitle: style.yTitle,
    legendHeight: legend?.height ?? 0,
    leftLabelWidth: maxTextWidth(yRange.ticks.map(yTick), {
      fontSize: CHART_FONT_SIZE.tick,
    }),
    bottomLabelHeight: CHART_FONT_SIZE.tick,
  });
  const { plot } = frame;
  const x = linearScale(
    [xRange.min, xRange.max],
    [plot.x + INSET, plot.x + plot.width - INSET]
  );
  const y = linearScale(
    [yRange.min, yRange.max],
    [plot.y + plot.height - INSET, plot.y + INSET]
  );
  const start = colorIndex(style.color);
  const colorOf = (index: number) => paletteColor(start, index);
  const drawn = fits.filter((entry) => entry.fit !== null);
  const clip = `${id}-clip`;
  return (
    <ChartSvg
      description={description}
      height={frame.height}
      id={id}
      label={label}
      width={frame.width}
    >
      <defs>
        <clipPath id={clip}>
          <rect
            height={px(plot.height)}
            width={px(plot.width)}
            x={px(plot.x)}
            y={px(plot.y)}
          />
        </clipPath>
      </defs>
      <ChartTitles frame={frame} />
      {legend ? (
        <SvgLegend
          keys={names.map((_, i) => ({
            color: colorOf(i),
            kind: "marker",
            shape: markerShape(i),
          }))}
          layout={legend}
          x={CHART_PADDING}
          y={frame.legendTop}
        />
      ) : null}
      <ValueAxis
        apa={style.apa}
        format={yTick}
        orient="left"
        plot={plot}
        scale={y}
        ticks={yRange.ticks}
      />
      <ValueAxis
        apa={style.apa}
        format={xTick}
        orient="bottom"
        plot={plot}
        scale={x}
        ticks={xRange.ticks}
      />
      <g clipPath={`url(#${clip})`}>
        {drawn.map(({ name, color, fit }) => {
          const line = fit as LinearFit;
          const at = (value: number) => line.intercept + line.slope * value;
          return (
            <line
              key={name}
              stroke={color}
              strokeDasharray="6 4"
              strokeWidth={1.5}
              x1={px(x(xRange.min))}
              x2={px(x(xRange.max))}
              y1={px(y(at(xRange.min)))}
              y2={px(y(at(xRange.max)))}
            />
          );
        })}
        {points.map((point) => (
          <Marker
            fill={colorOf(point.group)}
            key={point.key}
            shape={markerShape(point.group)}
            x={x(point.x)}
            y={y(point.y)}
          />
        ))}
        {style.valueLabels
          ? points.map((point) => (
              <text
                fill={CHART_INK.text}
                fontSize={CHART_FONT_SIZE.value}
                key={`label-${point.key}`}
                x={px(x(point.x) + 7)}
                y={px(y(point.y) - 6)}
              >
                {pointLabel(point.x, point.y, fmt)}
              </text>
            ))
          : null}
      </g>
      {showEquation && drawn.length > 0 ? (
        <EquationBlock
          fits={drawn}
          fmt={fmt}
          plot={plot}
          points={points.map((point) => [x(point.x), y(point.y)] as const)}
        />
      ) : null}
    </ChartSvg>
  );
}

export interface FitTableLabels {
  fitHeading: string;
  groupColumn: string;
  nColumn: string;
  slopeColumn: string;
  interceptColumn: string;
  rColumn: string;
  r2Column: string;
  noFit: string;
  fitNote: string;
}

/** The fitted lines as a table: n, slope, intercept, r and r² for each. */
export function FitTable({
  fits,
  labels,
  fmt,
}: {
  fits: ScatterFit[];
  labels: FitTableLabels;
  fmt: NumberFormat;
}) {
  const rows = fits.map(({ name, fit }) => {
    if (!fit) {
      return [
        name,
        MINUS,
        labels.noFit,
        labels.noFit,
        labels.noFit,
        labels.noFit,
      ];
    }
    const r = Number.isNaN(fit.r)
      ? MINUS
      : fmt(fit.r, { maximumFractionDigits: 3 });
    return [
      name,
      fmt(fit.n),
      formatCoefficient(fmt, fit.slope),
      formatCoefficient(fmt, fit.intercept),
      r,
      formatR2(fit, fmt),
    ];
  });
  return (
    <section aria-live="polite" className="space-y-3">
      <h2 className={WIDGET_H2}>{labels.fitHeading}</h2>
      <DataTable
        head={[
          labels.groupColumn,
          labels.nColumn,
          labels.slopeColumn,
          labels.interceptColumn,
          labels.rColumn,
          labels.r2Column,
        ]}
        rows={rows}
      />
      <p className={HINT}>{labels.fitNote}</p>
    </section>
  );
}
